import getLocalizedHref from '@/components/Link/getLocalizedHref'

import { match } from 'ts-pattern'

import { replaceUrlIfChanged } from './replace-url-if-changed'

const generateLink = (type: 'basket' | 'standingOrder' | 'order', id: string) => {
  const param = match(type)
    .with('order', () => 'orderId')
    .with('basket', () => 'id')
    .with('standingOrder', () => 'standingOrderId')
    .exhaustive()
  return `${window.location.origin}${window.location.pathname}?${param}=${id}`
}

export const generateBasketLink = (basketId: string) => generateLink('basket', basketId)

export const generateStandingOrderLink = (standingOrderId: string) =>
  generateLink('standingOrder', standingOrderId)

export const generateOrderLink = (orderId: string) => generateLink('order', orderId)

/**
 * Share URL for a team-order basket. Carries the join context the unauthenticated
 * login/signup flow keys off (?groupOrder=1&companyCode&companyName) — a bare ?id= link
 * would silently break that flow. companyCode/companyName mirror what the accounts
 * service embeds in Account.onboardingLink (signUpCode + name), read here from the
 * account fields directly; both are best-effort — the link still loads without them.
 */
export const generateCateringTeamOrderLink = (
  basketId: string,
  account?: { signUpCode?: string | null; name?: string | null } | null,
) => {
  const params = new URLSearchParams({ id: basketId, groupOrder: '1' })
  if (account?.signUpCode) params.set('companyCode', account.signUpCode)
  if (account?.name) params.set('companyName', account.name)
  return `${window.location.origin}${window.location.pathname}?${params.toString()}`
}

export const getBasketIdParam = (searchParams: { get(name: string): string | null } | null) =>
  searchParams?.get('id') || searchParams?.get('basketId') || ''

export const isCateringTeamOrderShareVisit = (
  searchParams: { get(name: string): string | null } | null,
) => searchParams?.get('groupOrder') === '1'

const SHARE_LINK_PARAMS = ['id', 'basketId', 'groupOrder', 'companyCode', 'companyName']
const EDIT_ORDER_PARAMS = ['orderId', 'source']

// History, not the router: Next syncs useSearchParams from its patched replaceState, while a
// router.replace can be discarded by an in-flight navigation (see useCateringTeamOrderEntry).
const clearParams = (names: string[]) => {
  const params = new URLSearchParams(window.location.search)
  if (!names.some((param) => params.has(param))) return
  names.forEach((param) => params.delete(param))
  replaceUrlIfChanged(params)
}

export const clearShareLinkParams = () => clearParams(SHARE_LINK_PARAMS)
export const clearEditOrderParams = () => clearParams(EDIT_ORDER_PARAMS)

/**
 * Login URL for an unauthenticated team-order share-link visitor. groupOrder/companyCode/
 * companyName ride along top-level (not only inside destination) so the login page can key
 * the join CTA off them without parsing the destination.
 */
export const buildCateringTeamOrderLoginHref = ({
  pathname,
  searchParams,
  locale,
}: {
  pathname: string
  searchParams: { get(name: string): string | null; toString(): string } | null
  locale?: string
}) => {
  const query = searchParams?.toString() ?? ''
  const loginParams = new URLSearchParams({
    destination: `${pathname}${query ? `?${query}` : ''}`,
    groupOrder: '1',
  })
  const companyCode = searchParams?.get('companyCode')
  const companyName = searchParams?.get('companyName')
  if (companyCode) loginParams.set('companyCode', companyCode)
  if (companyName) loginParams.set('companyName', companyName)
  return getLocalizedHref(`/authentication/login?${loginParams.toString()}`, locale)
}

/** Login URL for a logged-out visitor on an edit-order link; they land back on the edit page. */
export const buildEditOrderLoginHref = ({
  pathname,
  searchParams,
  locale,
}: {
  pathname: string
  searchParams: { toString(): string } | null
  locale?: string
}) => {
  const query = searchParams?.toString() ?? ''
  const loginParams = new URLSearchParams({
    destination: `${pathname}${query ? `?${query}` : ''}`,
  })
  return getLocalizedHref(`/authentication/login?${loginParams.toString()}`, locale)
}
